import { c as _c } from "react/compiler-runtime";
import { merge } from 'lodash';
import { queryClient } from 'api/reactQuery';
import { useMutation, useQuery, ExtractFnReturnType, MutationConfig, QueryConfig } from 'api/reactQuery';
import { buildListQueryKey } from 'shared/components/list/useListController';
import { commuteSectionCategoriesEndpoints } from '../commuteSectionCategories/endpoints';
import { getCommuteSections, createCommuteSection, updateCommuteSection, CommuteSectionCreateParams, CommuteSectionUpdateParams, getOneCommuteSection, getCommuteSectionResources, getCommuteSectionDraft, putCommuteSectionDraft, publishCommuteSectionDraft, discardCommuteSectionDraft, deleteManyCommuteSections, deleteOneCommuteSection, reorderCommuteSections, ReorderCommuteSectionsPayload } from './api';
const makeCommuteSectionsQueryKeys = {
  root: ['commute-sections'] as const,
  all: (queryParams: Parameters<typeof getCommuteSections>[0]) => [...makeCommuteSectionsQueryKeys.root, queryParams] as const,
  one: (sectionId: string, queryParams: Parameters<typeof getOneCommuteSection>[1]) => [...makeCommuteSectionsQueryKeys.root, sectionId, queryParams] as const,
  resources: (sectionId: string, queryParams: Parameters<typeof getCommuteSectionResources>[1]) => [...makeCommuteSectionsQueryKeys.root, sectionId, 'commute-resources', queryParams] as const,
  draft: (sectionId: string) => [...makeCommuteSectionsQueryKeys.root, sectionId, 'draft'] as const
};

/**
 * Publish and discard change what other caches say about the section: the
 * entity's hasPendingDraft everywhere, live contentJson on publish, and the
 * guides list's pending-draft count. The mutation hooks own that refresh —
 * the factory root partially matches every section query above, and the
 * list key covers the category view the guides list reads.
 */
function invalidateDraftAffectedCaches() {
  // The factory root partially matches every section query, draft keys included.
  queryClient.invalidateQueries({
    queryKey: makeCommuteSectionsQueryKeys.root
  });
  queryClient.invalidateQueries({
    queryKey: buildListQueryKey(commuteSectionCategoriesEndpoints.GET.All)
  });
}
export function useCommuteSections(queryParams, options) {
  const $ = _c(10);
  let paramsWithDefaultSort;
  let t0;
  let t1;
  if ($[0] !== queryParams) {
    paramsWithDefaultSort = merge({}, {
      sort: "+order"
    }, queryParams);
    t1 = useQuery;
    t0 = makeCommuteSectionsQueryKeys.all(paramsWithDefaultSort);
    $[0] = queryParams;
    $[1] = paramsWithDefaultSort;
    $[2] = t0;
    $[3] = t1;
  } else {
    paramsWithDefaultSort = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== paramsWithDefaultSort) {
    t2 = async () => getCommuteSections(paramsWithDefaultSort);
    $[4] = paramsWithDefaultSort;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t2,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t1(t3);
}
export function useOneCommuteSection(sectionId, queryParams, options) {
  const $ = _c(10);
  let t0;
  if ($[0] !== queryParams || $[1] !== sectionId) {
    t0 = makeCommuteSectionsQueryKeys.one(sectionId, queryParams);
    $[0] = queryParams;
    $[1] = sectionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryParams || $[4] !== sectionId) {
    t1 = async () => getOneCommuteSection(sectionId, queryParams);
    $[3] = queryParams;
    $[4] = sectionId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
}
export function useCommuteSectionResources(sectionId, queryParams, options) {
  const $ = _c(10);
  let t0;
  if ($[0] !== queryParams || $[1] !== sectionId) {
    t0 = makeCommuteSectionsQueryKeys.resources(sectionId, queryParams);
    $[0] = queryParams;
    $[1] = sectionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== queryParams || $[4] !== sectionId) {
    t1 = async () => getCommuteSectionResources(sectionId, queryParams);
    $[3] = queryParams;
    $[4] = sectionId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useQuery(t2);
}
export const useCreateCommuteSection = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export type UseUpdateCommuteSectionData = {
  commuteSectionId: string;
  data: CommuteSectionUpdateParams;
};
export const useUpdateCommuteSection = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp2,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteManyCommuteSections = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp3,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteOneCommuteSection = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp4,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useReorderCommuteSections = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp5,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export function useCommuteSectionDraft(sectionId, options) {
  const $ = _c(8);
  let t0;
  if ($[0] !== sectionId) {
    t0 = makeCommuteSectionsQueryKeys.draft(sectionId);
    $[0] = sectionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== sectionId) {
    t1 = async () => getCommuteSectionDraft(sectionId);
    $[2] = sectionId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export const usePutCommuteSectionDraft = options => {
  const $ = _c(5);
  let t0;
  if ($[0] !== options) {
    t0 = (...t1) => {
      const args = t1;
      invalidateDraftAffectedCaches();
      options?.onSettled?.(...args);
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: _temp6,
      ...options,
      onSettled: t0
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePublishCommuteSectionDraft = options => {
  const $ = _c(5);
  let t0;
  if ($[0] !== options) {
    t0 = (...t1) => {
      const args = t1;
      invalidateDraftAffectedCaches();
      options?.onSettled?.(...args);
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: _temp7,
      ...options,
      onSettled: t0
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useDiscardCommuteSectionDraft = options => {
  const $ = _c(5);
  let t0;
  if ($[0] !== options) {
    t0 = (...t1) => {
      const args = t1;
      invalidateDraftAffectedCaches();
      options?.onSettled?.(...args);
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: _temp8,
      ...options,
      onSettled: t0
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
function _temp(data) {
  return createCommuteSection(data);
}
function _temp2(t0) {
  const {
    commuteSectionId,
    data
  } = t0;
  return updateCommuteSection(commuteSectionId, data);
}
function _temp3(params) {
  return deleteManyCommuteSections(params);
}
function _temp4(commuteSectionId) {
  return deleteOneCommuteSection(commuteSectionId);
}
function _temp5(payload) {
  return reorderCommuteSections(payload);
}
function _temp6(t0) {
  const {
    commuteSectionId,
    draftContentJson
  } = t0;
  return putCommuteSectionDraft(commuteSectionId, draftContentJson);
}
function _temp7(commuteSectionId) {
  return publishCommuteSectionDraft(commuteSectionId);
}
function _temp8(commuteSectionId) {
  return discardCommuteSectionDraft(commuteSectionId);
}